import { forwardRef, HTMLAttributes, useMemo } from 'react'

import { getConnectionQualityIcon } from '@/src/assets/icons/util'

import { mergeProps } from '@/src/utils'

import type { ConnectionQualityIndicatorOptions } from '@/src/hooks'
import { useConnectionQualityIndicator } from '@/src/hooks'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */
export interface ConnectionQualityIndicatorProps
  extends HTMLAttributes<HTMLDivElement>,
    ConnectionQualityIndicatorOptions {}

/**
 * The `ConnectionQualityIndicator` shows the individual connection quality of a participant.
 *
 * @example
 * ```tsx
 * <ConnectionQualityIndicator />
 * ```
 * @public
 */
export const ConnectionQualityIndicator = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  ConnectionQualityIndicatorProps
>(function ConnectionQualityIndicator(props: ConnectionQualityIndicatorProps, ref) {
  const { className, quality } = useConnectionQualityIndicator(props)
  const { text_primary_color } = useAppSelector(selectBranding)

  const elementProps = useMemo(
    () => ({
      ...mergeProps(props, {
        style: {
          ...props?.style,
          color: props?.style?.color ?? text_primary_color,
        },
        className: `${props?.className || ''} ${className}`,
      }),
      'data-lk-quality': quality,
    }),
    [quality, props, className, text_primary_color],
  )
  return (
    <div ref={ref} {...elementProps}>
      {props.children ?? getConnectionQualityIcon(quality)}
    </div>
  )
})
